import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MenuItem as MenuItemType, Language } from '../types';
import { theme } from '../styles/theme';
import { getItemAvailability } from '../utils/availability';

const MenuItemContainer = styled.div<{ available: boolean }>`
  background: ${theme.colors.surface};
  border-radius: ${theme.shape.borderRadius}px;
  padding: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(2)};
  box-shadow: ${theme.elevation[2]};
  opacity: ${props => props.available ? 1 : 0.7};
  border-left: 4px solid ${props => props.available ? theme.colors.success : theme.colors.error};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  }
`;

const MenuItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing(2)};

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing(2)};
  }
`;

const MenuItemName = styled.h3`
  margin: 0;
  font-size: ${theme.typography.h5.fontSize};
  font-weight: ${theme.typography.h5.fontWeight};
  color: ${theme.colors.onSurface};
  line-height: ${theme.typography.h5.lineHeight};
  flex: 1;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.h6.fontSize};
  }
`;

const MenuItemDescription = styled.p`
  margin: ${theme.spacing(0.5)} 0 0 0;
  font-size: ${theme.typography.body2.fontSize};
  color: ${theme.colors.onSurfaceVariant};
  line-height: ${theme.typography.body2.lineHeight};
  font-style: italic;
  font-weight: ${theme.typography.body2.fontWeight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.caption.fontSize};
  }
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: ${theme.spacing(3)};
  min-width: 120px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    align-items: flex-start;
    margin-left: 0;
    margin-top: ${theme.spacing(2)};
  }
`;

const Price = styled.div`
  font-size: ${theme.typography.h4.fontSize};
  font-weight: ${theme.typography.h4.fontWeight};
  color: ${theme.colors.primary};
  text-align: right;

  @media (max-width: ${theme.breakpoints.mobile}) {
    text-align: left;
    font-size: ${theme.typography.h5.fontSize};
  }
`;

const AvailabilityBadge = styled.div<{ available: boolean }>`
  margin-top: ${theme.spacing(1)};
  padding: ${theme.spacing(0.5)} ${theme.spacing(1)};
  border-radius: ${theme.shape.borderRadius}px;
  font-size: ${theme.typography.caption.fontSize};
  font-weight: ${theme.typography.caption.fontWeight};
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  background: ${props => props.available ? theme.colors.success : theme.colors.error};
  color: ${props => props.available ? theme.colors.onSuccess : theme.colors.onError};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const StockInfo = styled.div`
  margin-top: ${theme.spacing(1)};
  font-size: ${theme.typography.caption.fontSize};
  color: ${theme.colors.onSurfaceVariant};
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing(1.5)};
  margin-top: ${theme.spacing(2)};
`;

const Tag = styled.span`
  background: ${theme.colors.surfaceVariant};
  color: ${theme.colors.onSurfaceVariant};
  padding: ${theme.spacing(1)} ${theme.spacing(2)};
  border-radius: ${theme.shape.borderRadius}px;
  font-size: ${theme.typography.caption.fontSize};
  font-weight: ${theme.typography.caption.fontWeight};
  border: 1px solid ${theme.colors.outline};

  &.organic {
    background: ${theme.colors.surfaceVariant};
    color: ${theme.colors.success};
    border-color: ${theme.colors.success};
  }

  &.vegan {
    background: ${theme.colors.surfaceVariant};
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
  }

  &.dry {
    background: ${theme.colors.surfaceVariant};
    color: ${theme.colors.warning};
    border-color: ${theme.colors.warning};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    padding: ${theme.spacing(0.5)} ${theme.spacing(1.5)};
  }
`;

const AllergenContainer = styled.div`
  margin-top: ${theme.spacing(1)};
  font-size: ${theme.typography.caption.fontSize};
  color: ${theme.colors.onSurfaceVariant};
  line-height: ${theme.typography.caption.lineHeight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.caption.fontSize};
  }
`;

interface MenuItemProps {
  item: MenuItemType;
  language: Language;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, language }) => {
  const { t } = useTranslation();
  const availability = getItemAvailability(item.id);

  return (
    <MenuItemContainer available={availability.available}>
      <MenuItemHeader>
        <div style={{ flex: 1 }}>
          <MenuItemName>{item.name[language]}</MenuItemName>
          {item.description && (
            <MenuItemDescription>{item.description[language]}</MenuItemDescription>
          )}
        </div>
        <PriceContainer>
          <Price>€{item.price.eur.toFixed(2)}</Price>
        </PriceContainer>
      </MenuItemHeader>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {(item.tags || item.allergens || item.additives) && (
            <TagsContainer>
              {item.tags?.map(tag => (
                <Tag key={tag} className={tag}>
                  {t(tag)}
                </Tag>
              ))}
            </TagsContainer>
          )}

          {(item.allergens || item.additives) && (
            <AllergenContainer>
              {item.allergens && item.allergens.length > 0 && (
                <div>
                  <strong>Allergens:</strong> {item.allergens.join(', ')}
                </div>
              )}
              {item.additives && item.additives.length > 0 && (
                <div>
                  <strong>Additives:</strong> {item.additives.join(', ')}
                </div>
              )}
            </AllergenContainer>
          )}

          {!availability.available && availability.estimatedRefill && (
            <StockInfo>
              Expected back: {availability.estimatedRefill}
            </StockInfo>
          )}
        </div>

        <AvailabilityBadge available={availability.available}>
          {availability.available ? 'Available' : 'Unavailable'}
        </AvailabilityBadge>
      </div>
    </MenuItemContainer>
  );
};

export default MenuItem;